import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Package, ShoppingCart, Plus, Search, Truck, CheckCircle2, Clock, XCircle,
  RefreshCw, Loader2, ChevronRight, User, ArrowRight, Minus, Send,
  Bandage, Syringe, Pill, Heart, Droplet, Thermometer
} from "lucide-react";

const ORDER_STATUS = {
  draft: { label: "Draft", color: "bg-gray-500" },
  pending_approval: { label: "Pending", color: "bg-yellow-500" },
  approved: { label: "Approved", color: "bg-blue-500" },
  submitted: { label: "Submitted", color: "bg-indigo-500" },
  processing: { label: "Processing", color: "bg-purple-500" },
  shipped: { label: "Shipped", color: "bg-cyan-500" },
  delivered: { label: "Delivered", color: "bg-green-500" },
  cancelled: { label: "Cancelled", color: "bg-red-500" }
};

const SUPPLY_ICONS = {
  wound_care: Bandage,
  syringes: Syringe,
  medications: Pill,
  cardiac: Heart,
  iv_supplies: Droplet,
  monitoring: Thermometer,
  default: Package
};

export default function StaffSuppliesPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  
  // Catalog
  const [catalog, setCatalog] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Cart
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  
  // Quick Order
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const [quickOrder, setQuickOrder] = useState({
    patient_name: "",
    patient_id: "",
    notes: "",
    priority: "normal"
  });
  const [recentOrders, setRecentOrders] = useState([]);
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.error("Please login to access supplies portal");
      navigate("/");
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCatalog(),
        loadRecentOrders()
      ]);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}&type=supplies` : "?type=supplies";
      const response = await api.get(`/dme-supplies/catalog${params}`);
      setCatalog(response.data.items || []);
    } catch (error) {
      console.error("Failed to load catalog:", error);
      // Mock data for demo
      setCatalog([
        { id: "sup1", name: "Gauze Pads 4x4 (100ct)", category: "wound_care", hcpcs_code: "A6219" },
        { id: "sup2", name: "Saline Flush Syringes", category: "syringes", hcpcs_code: "A4216" },
        { id: "sup3", name: "Tegaderm Film Dressing", category: "wound_care", hcpcs_code: "A6257" },
        { id: "sup4", name: "Alcohol Prep Pads (200ct)", category: "wound_care", hcpcs_code: "A4245" },
        { id: "sup5", name: "IV Start Kit", category: "iv_supplies", hcpcs_code: "A4221" },
        { id: "sup6", name: "Disposable Gloves (Box)", category: "ppe", hcpcs_code: "A4927" },
        { id: "sup7", name: "Chux Pads (100ct)", category: "incontinence", hcpcs_code: "A4554" },
        { id: "sup8", name: "Wound Cleanser Spray", category: "wound_care", hcpcs_code: "A6260" }
      ]);
    }
  };

  const loadRecentOrders = async () => {
    try {
      const response = await api.get("/dme-supplies/orders?limit=5");
      setRecentOrders(response.data.orders || []);
    } catch (error) {
      console.error("Failed to load recent orders:", error);
      setRecentOrders([]);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (!loading) loadCatalog();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const addToCart = (item) => {
    const existing = cart.find(c => c.item_id === item.id);
    if (existing) {
      setCart(cart.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, {
        item_id: item.id,
        item_name: item.name,
        hcpcs_code: item.hcpcs_code,
        quantity: 1,
        category: item.category
      }]);
    }
    toast.success(`Added ${item.name}`);
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(c => c.item_id !== itemId));
  };

  const updateCartQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }
    setCart(cart.map(c => c.item_id === itemId ? { ...c, quantity } : c));
  };

  const handleQuickOrder = async () => {
    if (!quickOrder.patient_name || cart.length === 0) {
      toast.error("Patient name and items are required");
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        patient_name: quickOrder.patient_name,
        patient_id: quickOrder.patient_id,
        notes: quickOrder.notes,
        priority: quickOrder.priority,
        items: cart,
        order_type: "supplies"
      };
      
      const response = await api.post("/dme-supplies/orders", orderData);
      toast.success(`Supply order ${response.data.order_number} created!`);
      setShowQuickOrder(false);
      setCart([]);
      setQuickOrder({ patient_name: "", patient_id: "", notes: "", priority: "normal" });
      loadRecentOrders();
    } catch (error) {
      toast.error("Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const getSupplyIcon = (category) => {
    const Icon = SUPPLY_ICONS[category] || SUPPLY_ICONS.default;
    return <Icon className="h-5 w-5" />;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="supplies-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-emerald-400 mb-4" />
          <p className="text-slate-400">Loading Supplies Portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="staff-supplies-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white py-5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 rounded-xl">
                <Package className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Medical Supplies</h1>
                <p className="text-emerald-100 text-sm">Quick order supplies for patients</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 relative"
                onClick={() => setShowCart(true)}
                data-testid="supplies-cart-btn"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Cart
                {cart.length > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-xs">{cart.length}</Badge>
                )}
              </Button>
              <Button 
                onClick={() => cart.length > 0 && setShowQuickOrder(true)} 
                className="bg-white text-emerald-600 hover:bg-emerald-50"
                disabled={cart.length === 0}
                data-testid="quick-order-btn"
              >
                <Send className="h-4 w-4 mr-2" />Quick Order
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Catalog */}
          <div className="lg:col-span-2 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search supplies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white"
                data-testid="supplies-search"
              />
            </div>

            {/* Supplies Grid */}
            <div className="grid sm:grid-cols-2 gap-3">
              {catalog.map((item, idx) => (
                <Card 
                  key={item.id || idx} 
                  className="bg-slate-800 border-slate-700 hover:border-emerald-500 transition-all cursor-pointer group"
                  onClick={() => addToCart(item)}
                  data-testid={`supply-item-${idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-emerald-500/20 rounded-lg group-hover:bg-emerald-500/30 transition-colors">
                        {getSupplyIcon(item.category)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{item.name}</p>
                        {item.hcpcs_code && (
                          <p className="text-xs text-slate-500">HCPCS: {item.hcpcs_code}</p>
                        )}
                      </div>
                      <Plus className="h-5 w-5 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {catalog.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No supplies found</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Cart Preview & Recent */}
          <div className="space-y-4">
            {/* Cart Preview */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="py-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-emerald-400" />
                  Current Cart ({cart.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0">
                {cart.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <p className="text-sm">Click supplies to add to cart</p>
                  </div>
                ) : (
                  <div className="space-y-2 pb-4">
                    {cart.slice(0, 5).map((item) => (
                      <div key={item.item_id} className="flex items-center justify-between text-sm p-2 bg-slate-700/50 rounded">
                        <span className="text-slate-300 truncate flex-1">{item.item_name}</span>
                        <div className="flex items-center gap-1 ml-2">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); updateCartQuantity(item.item_id, item.quantity - 1); }}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-6 text-center text-white">{item.quantity}</span>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); updateCartQuantity(item.item_id, item.quantity + 1); }}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {cart.length > 5 && (
                      <p className="text-xs text-slate-500 text-center">+{cart.length - 5} more items</p>
                    )}
                    <Button 
                      className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowQuickOrder(true)}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />Complete Order
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="py-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  Recent Orders
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0 pb-4">
                {recentOrders.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No recent orders</p>
                ) : (
                  <div className="space-y-2">
                    {recentOrders.slice(0, 5).map((order) => (
                      <div key={order.id} className="p-2 bg-slate-700/50 rounded text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{order.order_number}</span>
                          <Badge className={ORDER_STATUS[order.status]?.color} variant="secondary">
                            {ORDER_STATUS[order.status]?.label || order.status}
                          </Badge>
                        </div>
                        <p className="text-slate-400 text-xs mt-1">{order.patient_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Cart ({cart.length} items)</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {cart.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Cart is empty</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cart.map((item) => (
                  <div key={item.item_id} className="p-3 bg-slate-700/50 rounded-lg flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate">{item.item_name}</p>
                      {item.hcpcs_code && <p className="text-xs text-slate-500">{item.hcpcs_code}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-slate-600" onClick={() => updateCartQuantity(item.item_id, item.quantity - 1)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-6 text-center">{item.quantity}</span>
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0 border-slate-600" onClick={() => updateCartQuantity(item.item_id, item.quantity + 1)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => removeFromCart(item.item_id)}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700" 
              disabled={cart.length === 0}
              onClick={() => { setShowCart(false); setShowQuickOrder(true); }}
            >
              <ArrowRight className="h-4 w-4 mr-2" />Continue to Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Order Dialog */}
      <Dialog open={showQuickOrder} onOpenChange={setShowQuickOrder}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Quick Supply Order</DialogTitle>
            <DialogDescription className="text-slate-400">Enter patient details to complete order</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Patient Name *</Label>
              <Input
                value={quickOrder.patient_name}
                onChange={(e) => setQuickOrder({ ...quickOrder, patient_name: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="Full name"
                data-testid="quick-patient-name"
              />
            </div>
            <div>
              <Label>Patient ID (optional)</Label>
              <Input
                value={quickOrder.patient_id}
                onChange={(e) => setQuickOrder({ ...quickOrder, patient_id: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="MRN or ID"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={quickOrder.priority} onValueChange={(v) => setQuickOrder({ ...quickOrder, priority: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={quickOrder.notes}
                onChange={(e) => setQuickOrder({ ...quickOrder, notes: e.target.value })}
                className="bg-slate-700 border-slate-600"
                placeholder="Special instructions..."
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <p className="text-sm font-medium text-white mb-2">Items ({cart.length})</p>
              <div className="space-y-1 text-sm text-slate-300">
                {cart.slice(0, 5).map((item) => (
                  <div key={item.item_id} className="flex justify-between">
                    <span className="truncate">{item.item_name}</span>
                    <span className="text-slate-400">x{item.quantity}</span>
                  </div>
                ))}
                {cart.length > 5 && <p className="text-xs text-slate-500">+{cart.length - 5} more</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-600" onClick={() => setShowQuickOrder(false)}>Cancel</Button>
            <Button onClick={handleQuickOrder} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-quick-order">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Submit Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
